import { createContext, useContext, useEffect, useState } from "react";
import { useQuoteController } from "./QuoteControllerContext";
import { LLMResponse } from "../types";
import llmService from "../services/llmService";
import configService from "../services/configService";

interface LLMContextType {
    isShowingSidebar: boolean;
    lastRiskAnalysis: LLMResponse | null;
    setLastRiskAnalysis: (riskAnalysis: LLMResponse) => void;
    lastFlagAnalysis: LLMResponse | null;
    setLastFlagAnalysis: (flagAnalysis: LLMResponse) => void;
    lastConsistencyAnalysis: LLMResponse | null;
    setLastConsistencyAnalysis: (flagAnalysis: LLMResponse) => void;
    refreshConsistencyAnalysis: () => Promise<void>;
}

const LLMContext = createContext<LLMContextType | undefined>(undefined);

export const LLMContextProvider = ({ children }) => {
    const { pageState } = useQuoteController();

    const [isProduction, setIsProduction] = useState(null);
    const [isShowingSidebar, setIsShowingSidebar] = useState(false);

    const [riskAnalysis, setRiskAnalysis] = useState<LLMResponse | null>(null);
    const [flagAnalysis, setFlagAnalysis] = useState<LLMResponse | null>(null);
    const [consistencyAnalysis, setConsistencyAnalysis] = useState<LLMResponse | null>(null);

    useEffect(() => {
        const getLastRiskAnalysis = async () => {   
            const lastRiskAnalysis = await llmService.getLastRiskAnalysis();
            setRiskAnalysis(lastRiskAnalysis);
        }

        const fetchIsProduction = async () => {
            const deploymentMode = await configService.getDeploymentMode();
            if (deploymentMode) {
                setIsProduction(deploymentMode === "Production");
            }
        }
        fetchIsProduction();

        //Subscribe to LLMService to get the latest risk analysis (llmResponse)
        const unsubscribe = llmService.subscribe((llmResponse) => {
            if (llmResponse.consistencyReasoning) {
                setConsistencyAnalysis(llmResponse);
            } else {
                setRiskAnalysis(llmResponse);
            }
        })

        if (!riskAnalysis)
            getLastRiskAnalysis();

        return () => {
            unsubscribe();
        }
    }, []);

    useEffect(() => {
        if (!pageState)
            return;

        if (isProduction)
            return;

        setIsShowingSidebar(pageState.showSidebar);

        //TODO: FIX ME  (this is not a great solution, it results in refreshing it multiple times when not needed)
        if (pageState.showSidebar)
            refreshConsistencyAnalysis();

    }, [pageState])

    const refreshConsistencyAnalysis = async () => {
        const analysis = await llmService.getLastConsistencyAnalysis(); 
        setConsistencyAnalysis(analysis);
    }

    const value: LLMContextType = {
        isShowingSidebar,
        lastRiskAnalysis: riskAnalysis,
        setLastRiskAnalysis: setRiskAnalysis,
        lastFlagAnalysis: flagAnalysis,
        setLastFlagAnalysis: setFlagAnalysis,
        lastConsistencyAnalysis: consistencyAnalysis,
        setLastConsistencyAnalysis: setConsistencyAnalysis,
        refreshConsistencyAnalysis: refreshConsistencyAnalysis,
    }

    return (
        <LLMContext.Provider value={value}>
            {children}
        </LLMContext.Provider>
    );
};

export const useLLMContext = ():
    | LLMContextType
    | undefined => {
    return useContext(LLMContext);
};